import { type CompanyProfileMenuV2_organization$key } from '#app/__generated__/relay/CompanyProfileMenuV2_organization.graphql.js';
import { CircularIconButton } from '#app/components/Buttons/CircularIconButton.js';
import { Menu } from '#app/components/Menu/Menu.js';
import { MountedPortal } from '#app/components/MountedPortal/MountedPortal.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { FlagIcon, MoreIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { useCallback, useState } from 'react';
import { css, styled } from 'styled-components';

const StyledCircularIconButton = styled(CircularIconButton)`
  ${({ theme }) => css`
    border: none;

    ${theme.mediaQueries.md} {
      border: 1px solid ${theme.colorsV2.gray20};
    }
  `}
`;

export const CompanyProfileMenuV2 = ({
  organizationRef,
}: {
  readonly organizationRef: CompanyProfileMenuV2_organization$key;
}) => {
  const organization = useFragment<CompanyProfileMenuV2_organization$key>(
    graphql`
      fragment CompanyProfileMenuV2_organization on Organization {
        name
      }
    `,
    organizationRef,
  );
  const [isMenuOpen, setMenuOpen] = useState(false);
  const { track } = useAnalytics();

  const toggleMenu = useCallback(() => {
    setMenuOpen((state) => !state);
  }, [setMenuOpen]);

  const subject = encodeURIComponent(
    `Company Profile Issue - ${organization.name}`,
  );
  const mailtoLink = `mailto:hello@contra.com?subject=${subject}`;

  return (
    <Menu
      aria-label="Open share menu"
      isOpen={isMenuOpen}
      offset={[0, null]}
      placement="bottom-end"
      strategy="fixed"
      toggleMenu={toggleMenu}
    >
      <Menu.Reference>
        <StyledCircularIconButton
          aria-label="Open share menu"
          icon={MoreIcon}
          onClick={toggleMenu}
          size="xsmall"
          variant="V2TertiaryOutline"
        />
      </Menu.Reference>
      <MountedPortal>
        <Menu.Panel hasShadow={false}>
          <Menu.Item
            as="a"
            href={mailtoLink}
            icon={FlagIcon}
            onClick={() =>
              track('link', {
                action: 'tapped',
                link_name: 'demand_report_company_profile',
              })
            }
          >
            Report an issue
          </Menu.Item>
        </Menu.Panel>
      </MountedPortal>
    </Menu>
  );
};
